import { isProductionEnvironment } from "./environment";
import { RAINDROP_SESSION_TOKEN_KEY, URL_BACKEND } from "./constants";

import crypto from "crypto-js";

const memoryCache = new Map();

/**
 * Sends a request to the Raindrop API.
 *
 * @param {string} endpoint - The endpoint to hit.
 * @param {string} method - The HTTP method to use.
 * @param {object} body - The body of the request
 * @returns {Promise} - The response from the API.
 */
const _fetchRaindropAPI = async ({
  endpoint,
  method = "GET",
  body = null,
  formData = null,
  doUseCache = false,
  isGraphQL = false,
}) => {
  const key = crypto
    .MD5(`${method}:${URL_BACKEND}/${endpoint}:${JSON.stringify(body)}`)
    .toString();

  if (formData && body) {
    throw new Error(
      "(RD-2403) - You can't have both a body and a formData in the same request."
    );
  }

  if (doUseCache && memoryCache.has(key) && (method === "GET" || isGraphQL)) {
    return memoryCache.get(key);
  }

  if (body) {
    body = JSON.stringify(body);
  }

  let headers = {};
  if (formData === null) {
    headers = {
      "Content-Type": "application/json",
    };
  }

  const token = localStorage.getItem(RAINDROP_SESSION_TOKEN_KEY);
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${URL_BACKEND}/${endpoint}`, {
    method: method,
    credentials: "include",
    headers: headers,
    body: body || formData,
  });

  if (!response.ok) {
    throw new Error(`(RD-2404) - ${response.statusText} `);
  }

  const response_json = await response.json();

  if (doUseCache) {
    memoryCache.set(key, response_json);
  }

  return response_json;
};

export const fetchGraphQL = async (query, doUseCache) => {
  if (doUseCache === undefined) {
    doUseCache = false;
  }

  return _fetchRaindropAPI({
    endpoint: "graphql",
    method: "POST",
    body: {
      query: query,
    },
    doUseCache: doUseCache,
    isGraphQL: true,
  });
};

export const fetchAccountPlanPost = async ({
  accountId = null,
  sausages = null,
  isSupportingRaindrop = null,
  subunits = null,
  contributionDay = null,
}) => {
  let sausages_serialized =
    sausages &&
    sausages.map((sausage) => {
      return {
        organisation_id: sausage.organisation?.id,
        waterfall_id: sausage.waterfall?.id,
        percentage: sausage.percentage,
      };
    });

  return _fetchRaindropAPI({
    endpoint: `api/v1/account-plans`,
    method: "POST",
    body: {
      account_id: accountId,
      subunits: subunits,
      subunits_currency: "GBP",
      contribution_day: contributionDay,
      is_supporting_raindrop: isSupportingRaindrop,
      sausages: sausages_serialized,
    },
  });
};

export const fetchAccountPlans = async ({ accountId }) => {
  return fetch(`${URL_BACKEND}/api/v1/account-plans`).then((response) =>
    response.json()
  );
};

export const fetchAccountNotesPost = async ({ accountId, text }) => {
  return fetch(`${URL_BACKEND}/api/v1/accounts/${accountId}/notes`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      account_id: accountId,
      text: text,
    }),
  }).then((response) => response.json());
};

export const fetchAuthenticationLogin = async ({
  email,
  application = "raindrop",
}) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/authentication/${application}/login`,
    method: "POST",
    body: { email },
  });
};

export const fetchAuthenticationSignUp = async ({
  application = "raindrop",
  email,
  namePreferred,
  nameLegal,
  dateOfBirth,
  addressLine1,
  postcode,
  city,
}) => {
  return fetch(`${URL_BACKEND}/api/v1/authentication/${application}/signup`, {
    method: "POST",
    headers: {
      "Content-Type": "multipart/form-data",
    },
    body: JSON.stringify({
      email: email,
      name_preferred: namePreferred,
      name_legal: nameLegal,
      date_of_birth: dateOfBirth,
      address_line1: addressLine1,
      address_postcode: postcode,
      address_city: city,
    }),
  }).then((response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw new Error("(RD-2854) AccountUser could not be created");
    }
  });
};

export const fetchAuthenticationLogout = async ({
  application = "raindrop",
}) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/authentication/${application}/logout`,
    method: "POST",
  });
};

export const fetchAccountPlansExecute = async ({ accountPlanId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/account-plans/${accountPlanId}/execute`,
    method: "POST",
  });
};

export const fetchAccountPlanExecutions = async ({ accountId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/accounts/${accountId}/account-plan-executions`,
  });
};

export const fetchContactUs = async ({ name, email, message }) => {
  return fetch(`${URL_BACKEND}/api/v1/contact-us`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: name,
      email: email,
      message: message,
    }),
  }).then((response) => response.json());
};

export const fetchDeveloperToolsQueryDatabase = async ({ query }) => {
  return _fetchRaindropAPI({
    endpoint: "api/v1/developer-tools/query-database",
    method: "POST",
    body: {
      query: query,
    },
  });
};

export const fetchDeveloperToolsSendEmail = async ({ recipient, emailURL }) => {
  return fetch(`${URL_BACKEND}/api/v1/developer-tools/send-email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email_recipient: recipient,
      email_url: emailURL,
      email_subject: "(RD-3455) -Raindrop Developer Tools Change This",
    }),
  }).then((response) => response.json());
};

export const fetchDeveloperToolsScriptsGet = async () => {
  return _fetchRaindropAPI({ endpoint: "api/v1/developer-tools/scripts" });
};

export const fetchDeveloperToolsScriptsExecutePost = async ({
  scriptSlug,
  isDryRun = false,
}) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/developer-tools/scripts/${scriptSlug}/execute?dry=${isDryRun}`,
    method: "POST",
  });
};

export const fetchFeedbackPost = async ({ text, application }) => {
  return _fetchRaindropAPI({
    endpoint: "api/v1/feedback",
    method: "POST",
    body: {
      text: text,
      application: application,
    },
  });
};

export const fetchGenericEndpoint = async ({ endpoint, method = "GET" }) => {
  if (isProductionEnvironment()) {
    console.error(
      "🔴 (RD-736) - Woah woah woah.  You're trying to hit a generic endpoint in the production environment.  Let's not do that :)"
    );
    throw Error(
      "🔴 (RD-746) - Woah woah woah.  You're trying to hit a generic endpoint in the production environment.  Let's not do that :)"
    );
  }
  return _fetchRaindropAPI({ endpoint, method });
};

export const fetchGetStripeClientSecret = async ({ accountId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/accounts/${accountId}/get-stripe-client-secret`,
  });
};

export const fetchIncidentsPost = async () => {
  return fetch(`${URL_BACKEND}/api/v1/incidents`, {
    method: "POST",
  }).then((response) => response.json());
};

export const fetchIncidentGet = async ({ incidentId }) => {
  return fetch(`${URL_BACKEND}/api/v1/incidents/${incidentId}`).then(
    (response) => response.json()
  );
};

export const fetchIncidents = async () => {
  return fetch(`${URL_BACKEND}/api/v1/incidents`).then((response) =>
    response.json()
  );
};

export const fetchIncidentsDangerPauseApp = async ({ incidentId }) => {
  return fetch(
    `${URL_BACKEND}/api/v1/incidents/${incidentId}/danger/pause-app`,
    {
      method: "POST",
    }
  ).then((response) => response.json());
};

export const fetchIncidentsTimeline = async ({ incidentId }) => {
  return fetch(`${URL_BACKEND}/api/v1/incidents/${incidentId}/timeline`).then(
    (response) => response.json()
  );
};

export const fetchOrganisationsPut = async ({
  organisationId,
  name = null,
  urlWebsite = null,
  paymentProviderIdentifierStripe = null,
  src = null,
  status = null,
}) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}`,
    method: "PUT",
    body: {
      name: name,
      url_website: urlWebsite,
      payment_provider_identifier_stripe: paymentProviderIdentifierStripe,
      src: src,
      status: status,
    },
  });
};

export const fetchOrganisationIngestPost = async ({ charityNumber }) => {
  // This should probably not be a developer-tool.  Maybe just have an update for Orgs so you still create them manually.
  return _fetchRaindropAPI({
    endpoint: `/api/v1/developer-tools/ingest`,
    method: "POST",
    body: {
      charity_number: charityNumber,
    },
  });
};

export const fetchOrganisationGet = async ({ organisationId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}`,
    doUseCache: true,
  });
};

export const fetchOrganisationsActivityPost = async ({
  organisationId,
  heading,
}) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/activity`,
    method: "POST",
    body: {
      heading: heading,
    },
  });
};

export const fetchOrganisationLogoPost = async ({ organisationId, file }) => {
  const formData = new FormData();
  formData.append("file", file);

  return fetch(`${URL_BACKEND}/api/v1/organisations/${organisationId}/logo`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then((response) => response.json());
};

export const fetchOrganisationDetailGet = async ({ organisationId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/detail`,
    doUseCache: true,
  });
};

export const fetchOrganisationDetailPost = async ({
  organisationId,
  descriptionLong,
  media,
}) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/detail`,
    method: "POST",
    body: {
      description_long: descriptionLong,
    },
  });
};

export const fetchOrganisationDashboardGet = async ({ organisationId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/dashboard`,
  });
};

export const fetchOrganisationDetailImagePost = async ({
  organisationId,
  file,
  caption,
}) => {
  const formData = new FormData();
  formData.append("file", file);
  if (caption) {
    formData.append("caption", caption);
  }

  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/detail-image`,
    method: "POST",
    formData: formData,
  });
};

export const fetchAccountUploadProfilePost = async ({
  accountId = null,
  file,
}) => {
  const formData = new FormData();
  formData.append("file", file);

  if (accountId === null) {
    accountId = "self";
  }

  return _fetchRaindropAPI({
    endpoint: `api/v1/accounts/${accountId}/upload-profile`,
    method: "POST",
    formData: formData,
  });
};

export const fetchQuizQuestionUpload = async ({ categoryId, file, answer }) => {
  // "api/v1/waterfall/<str:waterfall_id>/post-upload"
  const formData = new FormData();
  formData.append("file", file);
  formData.append("answer", answer);
  formData.append("category_id", categoryId);

  return _fetchRaindropAPI({
    endpoint: `api/v1/quiz/post-question`,
    method: "POST",
    formData: formData,
  });
};

export const fetchWaterfallPostUpload = async ({ waterfallId, file, text }) => {
  // "api/v1/waterfall/<str:waterfall_id>/post-upload"
  const formData = new FormData();
  formData.append("file", file);
  formData.append("text", text);

  return _fetchRaindropAPI({
    endpoint: `api/v1/waterfall/${waterfallId}/post-upload`,
    method: "POST",
    formData: formData,
  });
};

export const fetchAdvertisementUpload = async ({ advertisementId, file }) => {
  const formData = new FormData();
  formData.append("file", file);

  return _fetchRaindropAPI({
    endpoint: `api/v1/advertisements/${advertisementId}/upload`,
    method: "POST",
    formData: formData,
  });
};

export const fetchOrganisationOrganisationUsersGet = async ({
  organisationId,
}) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/organisation-users`,
    doUseCache: true,
  });
};

export const fetchOrganisationTagPost = async ({ organisationId, value }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/tag`,
    method: "POST",
    body: {
      value: value,
    },
  });
};

export const fetchOrganisationUserGet = async ({ organisationUserId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisation-users/${organisationUserId}`,
  });
};

export const fetchOrganisationUserGetAll = async () => {
  return _fetchRaindropAPI({ endpoint: "api/v1/organisation-users" });
};

export const fetchOrganisationUserPost = async ({
  email,
  organisationId = null,
  nameLegal = null,
  namePreferred = null,
  dateOfBirth = null,
  addressLine1 = null,
  addressPostcode = null,
  addressCity = null,
}) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/organisation-users`,
    method: "POST",
    body: {
      organisation_id: organisationId,
      name_legal: nameLegal,
      name_preferred: namePreferred,
      email: email,
      date_of_birth: dateOfBirth,
      address_line1: addressLine1,
      address_postcode: addressPostcode,
      address_city: addressCity,
    },
  });
};

export const fetchOrganisationsTransferGet = async ({ organisationId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/organisation-transfer`,
  });
};

export const fetchOrganisationsTransferPost = async ({ organisationId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/organisations/${organisationId}/organisation-transfer`,
    method: "POST",
  });
};

export const fetchOrganisationTimeline = async ({ organisationId }) => {
  return fetch(
    `${URL_BACKEND}/api/v1/organisations/${organisationId}/timeline`
  ).then((response) => response.json());
};

export const fetchSupportUserGet = async ({ supportUserId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/support-users/${supportUserId}`,
  });
};

export const fetchUserGet = async ({ userId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/users/${userId}`,
  });
};

export const fetchUsersGet = async () => {
  return _fetchRaindropAPI({ endpoint: "api/v1/users" });
};

export const fetchUsersPut = async ({
  userId,
  nameLegal,
  namePreferred,
  email,
  username,
  dateOfBirth,
}) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/users/${userId}`,
    method: "PUT",
    body: {
      name_legal: nameLegal,
      name_preferred: namePreferred,
      email: email,
      username: username,
      date_of_birth: dateOfBirth,
    },
  });
};

export const fetchUserTimeline = async ({ userId }) => {
  return _fetchRaindropAPI({
    endpoint: `api/v1/users/${userId}/timeline`,
  });
};
